import React, { useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Mail, Lock, User } from "lucide-react";
import Emblem from "@/components/Emblem";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const input = "w-full rsp-card pl-10 pr-3 py-3 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/register", { name: form.name, email: form.email, password: form.password });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err, "Could not create account."));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex justify-center bg-[var(--rsp-bg)]">
        <div className="w-full max-w-[440px] px-6 flex flex-col items-center text-center pt-16 pb-12" data-testid="register-verify-sent">
          <Emblem size={120} />
          <div className="w-14 h-14 rounded-full bg-[var(--rsp-gold)]/15 border border-[var(--rsp-gold)]/40 flex items-center justify-center mt-6 mb-4">
            <Mail size={26} className="gold" />
          </div>
          <h1 className="heading gold" style={{ fontSize: "2rem" }}>Check your email</h1>
          <p className="text-[var(--rsp-gray)] text-sm mt-3 leading-relaxed">
            We sent a verification link to <strong className="text-[var(--rsp-cream)]">{form.email}</strong>. Tap it to activate your account, then sign in. The link expires in 24 hours.
          </p>
          <Link to="/login" className="btn-gold w-full py-3 mt-6 flex items-center justify-center gap-2" data-testid="verify-sent-login">Back to Sign In</Link>
          <p className="text-xs text-[var(--rsp-muted)] mt-4">Didn't get it? Check spam, or use "Resend verification" on the sign-in screen.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex justify-center bg-[var(--rsp-bg)]">
      <div className="w-full max-w-[440px] px-6 flex flex-col items-center text-center pt-14 pb-12">
        <Emblem size={120} />
        <div className="eyebrow mt-4" style={{ letterSpacing: "0.28em" }}>Create Account</div>
        <h1 className="heading gold" style={{ fontSize: "2.4rem" }}>Join RSP Riverside</h1>
        <p className="text-[var(--rsp-gray)] text-sm mt-1 mb-6">Register with any email — access is granted after admin approval.</p>

        <form onSubmit={submit} className="w-full space-y-3" data-testid="register-form">
          <div className="relative">
            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" />
            <input className={input} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
          </div>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" />
            <input className={input} type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="register-email" />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" />
            <input className={input} type="password" placeholder="Password (min 8 characters)" value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="register-password" />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" />
            <input className={input} type="password" placeholder="Confirm password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} required data-testid="register-confirm" />
          </div>
          <button type="submit" disabled={busy} className="btn-gold w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="register-submit">
            <UserPlus size={17} /> {busy ? "Creating…" : "Create Account"}
          </button>
        </form>

        <div className="text-sm text-[var(--rsp-gray)] mt-5">
          Already have an account? <Link to="/login" className="gold hover:brightness-110" data-testid="go-login-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
